import React, { useEffect, useRef, useCallback } from "react";
import "./conversationHUD.css";
import { useConversation } from "./ConversationContext";

export default function ConversationHUD() {
  const { near, bubble, activeId, interact, canInteract } = useConversation();

  // --- refs so the key handler always has current values ---
  const nearRef = useRef(near);
  const activeIdRef = useRef(activeId);
  const canInteractRef = useRef(canInteract);
  const interactRef = useRef(interact);

  useEffect(() => { nearRef.current = near; }, [near]);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);
  useEffect(() => { canInteractRef.current = canInteract; }, [canInteract]);
  useEffect(() => { interactRef.current = interact; }, [interact]);

  // guard so holding F doesn't spam interact
  const fDownRef = useRef(false);

  useEffect(() => {
    console.log("[HUD] mounted ✅");
    return () => console.log("[HUD] unmounted ❌");
  }, []);

  useEffect(() => {
    console.log("[HUD] near changed:", near);
  }, [near]);

  useEffect(() => {
    console.log("[HUD] bubble changed:", bubble);
  }, [bubble]);

  const fireInteract = useCallback(() => {
    const n = nearRef.current;
    const a = activeIdRef.current;

    // MUST be near to interact
    if (!n?.id) {
      console.log("[HUD] no near.id, ignoring");
      return;
    }

    // if something is already active, only allow interacting with THAT same npc
    if (a && a !== n.id) {
      console.log("[HUD] another npc is active, ignoring", { activeId: a, nearId: n.id });
      return;
    }

    const can = canInteractRef.current?.(n.id);
    if (!can) {
      console.log("[HUD] cooldown active, ignoring");
      return;
    }

    console.log("[HUD] interact firing:", n.id);
    interactRef.current?.(n.id, n.text || "Hey! Wanna play?");
  }, []);

  const handleTap = () => {
    console.log("[HUD] button clicked");
    fireInteract();
  };

  useEffect(() => {
    const isTypingTarget = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable === true;
    };

    const onKeyDown = (e) => {
      // debug line to prove the listener is firing:
      // (you can delete later)
      // console.log("[HUD] keydown:", e.code, e.key);

      if (isTypingTarget()) return;

      // use e.code (layout-safe)
      if (e.code !== "KeyF") return;

      // stop "hold key" spam
      if (fDownRef.current) return;
      fDownRef.current = true;

      fireInteract();
    };

    const onKeyUp = (e) => {
      if (e.code === "KeyF") fDownRef.current = false;
    };

    const onBlur = () => {
      // if the window loses focus while holding F, reset the guard
      fDownRef.current = false;
    };

    window.addEventListener("keydown", onKeyDown, true); // capture=true helps with canvas/pointerlock weirdness
    window.addEventListener("keyup", onKeyUp, true);
    window.addEventListener("blur", onBlur);

    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
      window.removeEventListener("blur", onBlur);
    };
  }, [fireInteract]);

  // Prompt shows only when near exists AND no other npc is active
  const canShowPrompt = !!near?.prompt && (!activeId || activeId === near?.id);

  return (
    <div className="s2-hud" style={{ position: "fixed", top: 20, left: 20, zIndex: 99999 }}>
      {canShowPrompt && (
        <button className="s2-prompt" onClick={handleTap} type="button">
          {near.prompt}
          <span className="s2-prompt-sub">Tap / Press F</span>
        </button>
      )}

      {bubble?.text && <div className="s2-bubble">{bubble.text}</div>}
    </div>
  );
}
